/**
 * Gaps Item Page
 * 
 * Full-page version of Gap form (Create/Edit)
 * 
 * Features:
 * - Cycle and Process selection (Process disabled until Cycle selected)
 * - Form fields for Gap details
 * - Risk Matrix Modal for Risk Ranking selection
 * - Save as Draft / Save buttons
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Select, Button, message, DatePicker, Typography, Dropdown, Checkbox, Tooltip } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { LeftOutlined, LoadingOutlined, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import ChevronDownIcon from '../../../assets/icons/ic-chevron-down.svg?react';
import ChevronRightIcon from '../../../assets/icons/ic-chevron-right.svg?react';
import CloseCircleIcon from '../../../assets/icons/ic-close-circle.svg?react';
import { RootState, AppDispatch } from '../../../store';
import {
  searchCycles,
  searchProcesses,
  CycleItem,
  ProcessItem,
} from '../../../services/hierarchyService';
import {
  Gap,
  GapStatus,
  GapType,
  PriorityLevel,
  createGap,
  updateGap,
  getAllGaps,
  CreateGapRequest,
  UpdateGapRequest,
  mapStatusToApi,
  mapGapTypeToApi,
  mapPriorityToApi,
} from '../../../services/gapService';
import { mapRiskRankingToApi, RiskRanking } from '../../../services/types';
import { setGaps } from '../../../store/slices/gapsSlice';
import { clearHierarchyData } from '../../../store/slices/hierarchySlice';
import { setApplications } from '../../../store/slices/auditPlanSlice';
import { getApplications } from '../../../services/auditPlanService';
import RiskMatrixModal from '../ex4/tab2/RiskMatrixModal';
import {
  getObjectives,
  Objective,
  parseObjectives,
} from '../../../services/riskAssessmentService';
import {
  getControlActivities,
  ControlActivity,
} from '../../../services/controlActivitiesService';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
} from '@syncfusion/ej2-react-grids';
import ConfirmModal from '../../../components/common/ConfirmModal';

const { TextArea } = Input;

// Interface for selected objectives with display title
interface SelectedObjective {
  id: string;
  processCode: string; // e.g., "A01"
  objectiveNumber: string; // e.g., "01"
  riskRanking: string;
  assertions: string[];
}

// Interface for selected controls with display title
interface SelectedControl {
  id: string;
  controlCode: string;
}
const { Title } = Typography;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  gap: 24px !important;
`;

const PageTitle = styled(Title)`
  margin: 0 0 24px 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  z-index: 1000;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
  margin-bottom: 16px;
`;

const SelectorsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
`;

const SelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CycleSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.7;
  }
`;

const CycleText = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
`;

const ChevronIcon = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  color: ${props => props.$color || '#6B7280'};
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  svg path {
    stroke: ${props => props.$color || '#6B7280'};
  }
`;

const ProcessSelectorsWrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.7 : 1};
`;

const ProcessChipDisplay = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 28px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 12px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
`;


const DropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  width: 346px;
  max-height: 400px;
  overflow-y: auto;
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 6px;
  height: 38px;
  
  .ant-input {
    font-family: Inter;
    font-size: 15px;
  }
`;

const DropdownSection = styled.div`
  margin-bottom: 8px;
`;

const DropdownSectionLabel = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6B7280;
  padding: 8px 12px 8px 12px;
`;

const CycleDropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  background: ${props => props.$selected ? '#EFF6FF' : 'white'};

  &:hover {
    background: #F3F4F6;
  }
`;

const CycleDropdownItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
  flex: 1;
`;

const CycleDropdownItemIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProcessDropdownItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  padding: 4px 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ProcessDropdownItemChip = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  height: 28px;
  border-radius: 50px;
  background: ${props => props.$selected ? '#2563EB' : '#EFF6FF'};
  transition: all 0.3s;

  &:hover {
    opacity: 0.9;
  }
`;

const ProcessDropdownItemText = styled.span<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.005em;
  color: ${props => props.$selected ? '#FFFFFF' : '#1E40AF'};
  flex: 1;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
`;

const EmptyStateText = styled.div`
  font-family: Inter;
  font-size: 16px;
  color: #6B7280;
  text-align: center;
`;

const EmptyStateSubtext = styled.div`
  font-family: Inter;
  font-size: 14px;
  color: #9CA3AF;
  text-align: center;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #F3F4F6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6B7280;

  &:hover {
    background: #E5E7EB;
    color: #1F2937;
  }

  .anticon {
    font-size: 16px;
  }
`;

const PageTitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const PageTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GapIdTitle = styled.h2`
  margin: 0;
  font-family: Inter;
  font-weight: 600;
  font-size: 24px;
  color: #1F2937;
`;

const ProcessOwnerChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #EFF6FF;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1E40AF;
`;

const ProcessOwnerAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #3B82F6;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 100px;
`;

const Section = styled.div`
  margin-top: 24px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #1F2937;
  margin: 0 0 16px 0;
`;

const IdentitySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const IdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IdentityLabel = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
  min-width: 80px;
`;

const CycleChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #ECFDF5;
  color: #047857;
`;

const ProcessChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #EFF6FF;
  color: #1E40AF;
`;

const GapIdChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: #FEF3C7;
  color: #92400E;
`;

const StatusChip = styled.span<{ $status: GapStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case 'Open': return '#4AB7B6';
      case 'Draft': return '#E5B454';
      case 'Archived': return '#9CA3AF';
      default: return '#F3F4F6';
    }
  }};
  color: white;

  &::before {
    content: '●';
    font-size: 8px;
  }
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid #E5E7EB;
  
  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled.label<{ $isDisabled?: boolean }>`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  min-width: 200px;
  max-width: 200px;
  color: ${props => props.$isDisabled ? '#9CA3AF' : '#1F2937'};
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding-top: 4px;
`;

const RequiredMark = styled.span`
  color: #DC2626;
`;

const ReadOnlyBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #9CA3AF;
  background: #F3F4F6;
  padding: 2px 6px;
  border-radius: 4px;
`;

const StyledInput = styled(Input)`
  flex: 1;
  border: none;
  border-radius: 0;
  padding: 0;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #1F2937;
  box-shadow: none !important;
  
  &:focus, &:hover {
    border: none;
    box-shadow: none;
  }
  
  &:disabled {
    background: transparent;
    color: #6B7280;
    cursor: default;
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

const StyledTextArea = styled(TextArea)`
  flex: 1;
  border: none !important;
  border-radius: 0;
  padding: 0;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #1F2937;
  box-shadow: none !important;
  resize: none;
  
  &:focus, &:hover {
    border: none !important;
    box-shadow: none !important;
  }
  
  &:disabled {
    background: transparent;
    color: #6B7280;
    cursor: default;
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

const StyledSelect = styled(Select)`
  flex: 1;
  margin-top: -8px;
  
  .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    height: auto !important;
    min-height: auto !important;
    background: transparent !important;
  }
  
  .ant-select-selection-item {
    font-family: Inter;
    font-weight: 400;
    font-size: 14px;
    color: #1F2937;
    padding: 0 !important;
  }
  
  .ant-select-arrow {
    right: 0;
    color: #6B7280;
  }

  &.ant-select-disabled {
    .ant-select-selector {
      background: transparent !important;
    }
    .ant-select-selection-item {
      color: #6B7280;
    }
  }
`;

const StyledDatePicker = styled(DatePicker)`
  flex: 1;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  
  .ant-picker-input {
    input {
      font-family: Inter;
      font-weight: 400;
      font-size: 14px;
      color: #1F2937;
    }
  }
  
  &:hover, &.ant-picker-focused {
    border: none !important;
    box-shadow: none !important;
  }
`;

// Custom Tag Component for Application Select
const ApplicationSelectTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #F3F4F6;
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 1;
  color: #374151;
  margin: 2px;
`;

const ApplicationCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

const RiskRankingChip = styled.span<{ $level: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: white;

  ${props => {
    switch (props.$level) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      default:
        return `
          border: 1px solid #9CA3AF;
          color: #9CA3AF;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    font-size: 10px;
  }
`;

const RiskChipHelper = styled.span`
  font-family: Inter;
  font-size: 12px;
  color: #9CA3AF;
  margin-left: 8px;
`;

// Objectives Search Mode Styled Components
const ObjectivesSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 100px;
  margin-top: -16px;
`;

const ObjectivesSearchHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Grid wrapper for objectives search
const ObjectivesGridWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);  
  overflow: hidden;
  padding: 0 0 0px 2px;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 8px;
    font-size: 12px;
    color: #1F2937;
    white-space: normal !important;
    vertical-align: top;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  /* Prevent dark hover/active state */
  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const SelectedObjectiveTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  margin: 2px;
`;

const ObjectiveProcessCode = styled.span`
  font-weight: 600;
  margin-right: 4px;
`;

const ObjectiveNumbersGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ObjectiveNumberCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  background: transparent;
  color: #2563EB;
  border: 1px solid #D1D5DB;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
`;

const SelectedControlTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #F0FDF4;
  color: #166534;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  margin: 2px;
`;

const ObjectivesFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  cursor: pointer;
  min-height: 32px;
  flex-wrap: wrap;
`;

const ObjectivesExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  cursor: pointer;
  margin-right: 0px;

  &:hover {
    color: #2563EB;
  }
`;

const ControlsFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  cursor: pointer;
  min-height: 32px;
  flex-wrap: wrap;
`;

const ControlsExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  cursor: pointer;
  margin-right: 0px;

  &:hover {
    color: #2563EB;
  }
`;

// Controls Grid Styled Components
const GrayChip = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: white;
  color: #374151;
  border: 1px solid #E5E7EB;
`;

const CtrlRiskRankingChip = styled.span<{ $level: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: white;

  ${props => {
    switch (props.$level) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #22C55E;
          color: #22C55E;
        `;
      default:
        return `
          border: 1px solid #6B7280;
          color: #6B7280;
        `;
    }
  }}

  &::before {
    content: '●';
    margin-right: 4px;
    font-size: 8px;
  }
`;

const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const CellContent = styled.div`
  font-family: Inter;
  font-size: 13px;
  color: #1F2937;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-break: break-word;

  p {
    margin: 0;
  }
`;

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-checkbox-wrapper {
    .ant-checkbox-inner {
      width: 20px;
      height: 20px;
      border-radius: 4px;
    }

    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: #2563EB;
      border-color: #2563EB;
    }
  }
`;

// Chip for Control Risk Ranking in objectives grid
const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Low':
        return `
          border: 1px solid #22C55E;
          color: #22C55E;
        `;
      default:
        return `
          border: 1px solid #6B7280;
          color: #6B7280;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const AssertionChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: #FEF3C7;
  color: #92400E;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
`;

// Tooltip components for multiple assertions
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #FEF9C3;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #854D0E;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #FDE68A;
    transform: translateX(2px);
  }
`;

// Footer
const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 260px;
  right: 0;
  background: white;
  border-top: 1px solid #E5E7EB;
  padding: 16px 32px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  z-index: 100;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const StyledOutlinedButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #2563EB;
  color: #2563EB;

  &:disabled {
    color: #9CA3AF !important;
    border-color: #D1D5DB !important;
  }

  &:hover {
    border-color: #1D4ED8 !important;
    color: #1D4ED8 !important;
  }
`;

const StyledFilledButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  background: #2563EB;
  border-color: #2563EB;
  color: #FFFFFF;
  
  &:hover {
    background: #1D4ED8 !important;
    border-color: #1D4ED8 !important;
    color: #FFFFFF !important;
  }

  &:disabled {
    color: #9CA3AF !important;
    border-color: #D1D5DB !important;
  }
`;

// ==================== MAIN COMPONENT ====================

const GapItemPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { gapId } = useParams<{ gapId: string }>();
  const [form] = Form.useForm();

  // Watch form values for validation
  const gapDescription = Form.useWatch('gapDescription', form);
  const riskDescription = Form.useWatch('riskDescription', form);
  const gapOwner = Form.useWatch('gapOwner', form);

  // Determine mode based on URL
  const mode = gapId ? 'edit' : 'create';
  const isEdit = mode === 'edit';

  // Get current annual report and gaps from Redux
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { gaps, lastFetchedReportId } = useSelector((state: RootState) => state.gaps);
  const { applications, applicationsLastFetchedReportId } = useSelector(
    (state: RootState) => state.auditPlan
  );
  const { isAdmin } = useSelector((state: RootState) => state.auth);

  // Find gap data from Redux in edit mode
  const gapData = isEdit ? gaps.find(g => g.id === gapId) : null;
  
  // Check if gap is archived (read-only mode)
  const isArchived = isEdit && gapData?.status === 'Archived';

  // Loading states
  const [isLoadingGap, setIsLoadingGap] = useState(false);
  const [gapNotFound, setGapNotFound] = useState(false);
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false);
  const [isSavingAsActive, setIsSavingAsActive] = useState(false);

  // Gap data for edit mode
  const [existingGap, setExistingGap] = useState<Gap | null>(null);

  // Cycle/Process selection
  const [cycleDropdownOpen, setCycleDropdownOpen] = useState(false);
  const [processDropdownOpen, setProcessDropdownOpen] = useState(false);
  const [cycleSearchQuery, setCycleSearchQuery] = useState('');
  const [processSearchQuery, setProcessSearchQuery] = useState('');
  const [cycles, setCycles] = useState<CycleItem[]>([]);
  const [processes, setProcesses] = useState<ProcessItem[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<CycleItem | null>(null);
  const [selectedProcess, setSelectedProcess] = useState<ProcessItem | null>(null);
  const [isLoadingCycles, setIsLoadingCycles] = useState(false);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);

  // Risk Matrix Modal
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);

  // Form values
  const [gapRiskRanking, setGapRiskRanking] = useState<RiskRanking | 'Unranked'>('Unranked');
  const [riskRatingSeverityIndex, setRiskRatingSeverityIndex] = useState<number | null>(null);
  const [riskRatingLikelihoodIndex, setRiskRatingLikelihoodIndex] = useState<number | null>(null);
  const [managementPrioritization, setManagementPrioritization] = useState<PriorityLevel | null>(null);
  const [gapType, setGapType] = useState<GapType | null>(null);
  const [status, setStatus] = useState<GapStatus>('Draft');
  const [isArchiving, setIsArchiving] = useState(false);

  // Confirmation modals
  const [openGapModalOpen, setOpenGapModalOpen] = useState(false);
  const [archiveGapModalOpen, setArchiveGapModalOpen] = useState(false);

  // Objectives search mode states
  const [objectivesSearchMode, setObjectivesSearchMode] = useState(false);
  const [objSearchCycle, setObjSearchCycle] = useState<CycleItem | null>(null);
  const [objSearchProcess, setObjSearchProcess] = useState<ProcessItem | null>(null);
  const [isSearchingCycles, setIsSearchingCycles] = useState(false);
  const [isSearchingProcesses, setIsSearchingProcesses] = useState(false);
  const [objSearchCycleResults, setObjSearchCycleResults] = useState<CycleItem[]>([]);
  const [objSearchProcessResults, setObjSearchProcessResults] = useState<ProcessItem[]>([]);
  const [objSearchObjectives, setObjSearchObjectives] = useState<Objective[]>([]);
  const [objSearchLoading, setObjSearchLoading] = useState(false);
  const [tempSelectedObjectives, setTempSelectedObjectives] = useState<SelectedObjective[]>([]);
  const [selectedObjectives, setSelectedObjectives] = useState<SelectedObjective[]>([]);
  const [objCycleSearchTerm, setObjCycleSearchTerm] = useState('');
  const [objProcessSearchTerm, setObjProcessSearchTerm] = useState('');
  const [objCycleDropdownOpen, setObjCycleDropdownOpen] = useState(false);
  const [objProcessDropdownOpen, setObjProcessDropdownOpen] = useState(false);

  // Controls search mode states
  const [controlsSearchMode, setControlsSearchMode] = useState(false);
  const [ctrlSearchCycle, setCtrlSearchCycle] = useState<CycleItem | null>(null);
  const [ctrlSearchProcess, setCtrlSearchProcess] = useState<ProcessItem | null>(null);
  const [isSearchingCtrlCycles, setIsSearchingCtrlCycles] = useState(false);
  const [isSearchingCtrlProcesses, setIsSearchingCtrlProcesses] = useState(false);
  const [ctrlSearchCycleResults, setCtrlSearchCycleResults] = useState<CycleItem[]>([]);
  const [ctrlSearchProcessResults, setCtrlSearchProcessResults] = useState<ProcessItem[]>([]);
  const [ctrlSearchControls, setCtrlSearchControls] = useState<ControlActivity[]>([]);
  const [ctrlSearchLoading, setCtrlSearchLoading] = useState(false);
  const [tempSelectedControls, setTempSelectedControls] = useState<SelectedControl[]>([]);
  const [selectedControls, setSelectedControls] = useState<SelectedControl[]>([]);
  const [ctrlCycleSearchTerm, setCtrlCycleSearchTerm] = useState('');
  const [ctrlProcessSearchTerm, setCtrlProcessSearchTerm] = useState('');
  const [ctrlCycleDropdownOpen, setCtrlCycleDropdownOpen] = useState(false);
  const [ctrlProcessDropdownOpen, setCtrlProcessDropdownOpen] = useState(false);

  // Applications state
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<string[]>([]);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);

  // Load gaps list if not already loaded (for edit mode)
  useEffect(() => {
    const loadGapsList = async () => {
      if (!selectedReport?.annualReportId) return;

      // Check if we need to fetch (no data or different report)
      const needsFetch =
        gaps.length === 0 ||
        lastFetchedReportId !== selectedReport.annualReportId;

      if (!needsFetch) return;

      setIsLoadingGap(isEdit);
      try {
        const response = await getAllGaps({
          annualReportId: selectedReport.annualReportId,
        });
        dispatch(setGaps({
          data: response.data,
          totalCount: response.totalCount,
          reportId: selectedReport.annualReportId
        }));
      } catch (error) {
        console.error('Failed to load gaps:', error);
        message.error('Failed to load gaps');
      } finally {
        setIsLoadingGap(false);
      }
    };

    loadGapsList();
  }, [selectedReport?.annualReportId, gaps.length, lastFetchedReportId, dispatch]);

  // Fetch applications on page load if not cached
  useEffect(() => {
    const fetchApplicationsData = async () => {
      if (!selectedReport?.annualReportId) return;

      // Check if we already have cached data for this report
      if (applicationsLastFetchedReportId === selectedReport.annualReportId && applications.length > 0) {
        // console.log('✅ Using cached applications data');
        return;
      }

      setIsLoadingApplications(true);
      try {
        const response = await getApplications({
          annualReportId: selectedReport.annualReportId,
          sorted: [{name: "Name", direction: "ascending"}]
        });
        dispatch(setApplications({
          data: response.data,
          totalCount: response.totalCount,
          reportId: selectedReport.annualReportId,
        }));
      } catch (error) {
        console.error('Failed to load applications:', error);
      } finally {
        setIsLoadingApplications(false);
      }
    };

    fetchApplicationsData();
  }, [selectedReport?.annualReportId, applicationsLastFetchedReportId, applications.length, dispatch]);

  // Initialize selected applications in edit mode
  useEffect(() => {
    if (isEdit && gapData?.applications) {
      setSelectedApplicationIds(gapData.applications.map(app => app.id));
    }
  }, [isEdit, gapData]);

  useEffect(() => {
    if (!isEdit) {
      setTimeout(() => {
        setCycleDropdownOpen(true);
      }, 200);
    }
  }, [isEdit]);

  // Check if gap exists in edit mode
  useEffect(() => {
    if (isEdit && !isLoadingGap) {
      if (gaps.length > 0 && !gapData) {
        // List is loaded but gap not found
        setGapNotFound(true);
      } else {
        setGapNotFound(false);
      }
    }
  }, [isEdit, gapData, gaps.length, isLoadingGap]);

  // Initialize form when gap data is available (edit mode)
  useEffect(() => {
    if (isEdit && gapData) {
      setExistingGap(gapData);
      setStatus(gapData.status);
      setGapRiskRanking(gapData.gapRiskRanking);
      setRiskRatingSeverityIndex(gapData.riskRatingSeverityIndex ?? null);
      setRiskRatingLikelihoodIndex(gapData.riskRatingLikelihoodIndex ?? null);
      setManagementPrioritization(gapData.managementPrioritization);
      setGapType(gapData.gapType);

      // Set cycle and process
      setSelectedCycle({
        cycleId: gapData.cycle.id,
        cycleCode: gapData.cycle.code,
        cycleName: gapData.cycle.name,
      });
      setSelectedProcess({
        processId: gapData.linkedProcess.id,
        processCode: gapData.linkedProcess.code,
        processName: gapData.linkedProcess.name,
        riskAssessmentId: gapData.linkedProcess.riskAssessmentId, // Not needed for Gap
        processOwnerId: gapData.processOwner.id,
        processOwnerName: gapData.processOwner.name,
      });

      // Set form values
      form.setFieldsValue({
        gapDescription: gapData.gapDescription,
        riskDescription: gapData.riskDescription,
        gapOwner: gapData.gapOwner,
        remediationRecommendation: gapData.remediationRecommendation,
        managementResponse: gapData.managementResponse,
        dateIdentified: dayjs(gapData.dateIdentified),
        gapClosureDate: gapData.gapClosureDate ? dayjs(gapData.gapClosureDate) : null,
      });

      // Set selected objectives from relatedObjectives
      if (gapData.relatedObjectives && gapData.relatedObjectives.length > 0) {
        const selectedObjs: SelectedObjective[] = gapData.relatedObjectives.map((obj) => ({
          id: obj.id,
          processCode: obj.processCode || '',
          objectiveNumber: obj.objectiveNumber || '',
          riskRanking: 'Low', // Default, will be updated when fetching objectives
          assertions: [],
        }));
        setSelectedObjectives(selectedObjs);
      }

      // Set selected controls from relatedControls
      if (gapData.relatedControls && gapData.relatedControls.length > 0) {
        const selectedCtrls: SelectedControl[] = gapData.relatedControls.map((ctrl) => ({
          id: ctrl.id,
          controlCode: ctrl.code || ctrl.name,
        }));
        setSelectedControls(selectedCtrls);
      }
    }
  }, [isEdit, gapData, form]);

  // Load cycles on mount
  useEffect(() => {
    const loadCycles = async () => {
      if (!selectedReport?.annualReportId) return;

      setIsLoadingCycles(true);
      try {
        const result = await searchCycles({
          annualReportId: selectedReport.annualReportId,
          searchTerm: '',
        });
        setCycles(result);
      } catch (error) {
        console.error('Error loading cycles:', error);
      } finally {
        setIsLoadingCycles(false);
      }
    };

    loadCycles();
  }, [selectedReport?.annualReportId]);

  // Load processes when cycle is selected
  useEffect(() => {
    const loadProcesses = async () => {
      if (!selectedReport?.annualReportId || !selectedCycle) {
        setProcesses([]);
        return;
      }

      setIsLoadingProcesses(true);
      try {
        const result = await searchProcesses({
          annualReportId: selectedReport.annualReportId,
          cycleIds: [selectedCycle.cycleId],
          searchTerm: '',
        });
        setProcesses(result);
      } catch (error) {
        console.error('Error loading processes:', error);
      } finally {
        setIsLoadingProcesses(false);
      }
    };

    loadProcesses();
  }, [selectedReport?.annualReportId, selectedCycle]);

  // Search cycles for objectives search mode
  useEffect(() => {
    if (!objectivesSearchMode || !selectedReport?.annualReportId) return;

    const fetchObjCyclesData = async () => {
      setIsSearchingCycles(true);
      try {
        const results = await searchCycles({
          annualReportId: selectedReport.annualReportId,
          searchTerm: objCycleSearchTerm,
        });
        setObjSearchCycleResults(results);
      } catch (error) {
        console.error('Error searching cycles:', error);
        setObjSearchCycleResults([]);
      } finally {
        setIsSearchingCycles(false);
      }
    };

    const timeoutId = setTimeout(fetchObjCyclesData, 300);
    return () => clearTimeout(timeoutId);
  }, [objectivesSearchMode, objCycleSearchTerm, selectedReport?.annualReportId]);

  // Search processes for objectives search mode
  useEffect(() => {
    if (!objectivesSearchMode || !objSearchCycle || !selectedReport?.annualReportId) return;

    const fetchObjProcessesData = async () => {
      setIsSearchingProcesses(true);
      try {
        const results = await searchProcesses({
          annualReportId: selectedReport.annualReportId,
          cycleIds: [objSearchCycle.cycleId],
          searchTerm: objProcessSearchTerm,
        });
        setObjSearchProcessResults(results);
      } catch (error) {
        console.error('Error searching processes:', error);
        setObjSearchProcessResults([]);
      } finally {
        setIsSearchingProcesses(false);
      }
    };

    const timeoutId = setTimeout(fetchObjProcessesData, 300);
    return () => clearTimeout(timeoutId);
  }, [objectivesSearchMode, objProcessSearchTerm, objSearchCycle, selectedReport?.annualReportId]);

  // Search cycles for controls search mode
  useEffect(() => {
    if (!controlsSearchMode || !selectedReport?.annualReportId) return;

    const fetchCtrlCyclesData = async () => {
      setIsSearchingCtrlCycles(true);
      try {
        const results = await searchCycles({
          annualReportId: selectedReport.annualReportId,
          searchTerm: ctrlCycleSearchTerm,
        });
        setCtrlSearchCycleResults(results);
      } catch (error) {
        console.error('Error searching cycles for controls:', error);
        setCtrlSearchCycleResults([]);
      } finally {
        setIsSearchingCtrlCycles(false);
      }
    };

    const timeoutId = setTimeout(fetchCtrlCyclesData, 300);
    return () => clearTimeout(timeoutId);
  }, [controlsSearchMode, ctrlCycleSearchTerm, selectedReport?.annualReportId]);

  // Search processes for controls search mode
  useEffect(() => {
    if (!controlsSearchMode || !ctrlSearchCycle || !selectedReport?.annualReportId) return;

    const fetchCtrlProcessesData = async () => {
      setIsSearchingCtrlProcesses(true);
      try {
        const results = await searchProcesses({
          annualReportId: selectedReport.annualReportId,
          cycleIds: [ctrlSearchCycle.cycleId],
          searchTerm: ctrlProcessSearchTerm,
        });
        setCtrlSearchProcessResults(results);
      } catch (error) {
        console.error('Error searching processes for controls:', error);
        setCtrlSearchProcessResults([]);
      } finally {
        setIsSearchingCtrlProcesses(false);
      }
    };

    const timeoutId = setTimeout(fetchCtrlProcessesData, 300);
    return () => clearTimeout(timeoutId);
  }, [controlsSearchMode, ctrlProcessSearchTerm, ctrlSearchCycle, selectedReport?.annualReportId]);

  // Filtered cycles based on search
  const filteredCycles = useMemo(() => {
    if (!cycleSearchQuery) return cycles;
    const query = cycleSearchQuery.toLowerCase();
    return cycles.filter(
      c => c.cycleCode.toLowerCase().includes(query) || c.cycleName.toLowerCase().includes(query)
    );
  }, [cycles, cycleSearchQuery]);

  // Filtered processes based on search
  const filteredProcesses = useMemo(() => {
    if (!processSearchQuery) return processes;
    const query = processSearchQuery.toLowerCase();
    return processes.filter(
      p => p.processCode.toLowerCase().includes(query) || p.processName.toLowerCase().includes(query)
    );
  }, [processes, processSearchQuery]);

  // Check if all required fields are filled
  const isAllRequiredFieldsFilled = useMemo(() => {
    const hasDescription = gapDescription?.trim() || '';
    const hasRiskDescription = riskDescription?.trim() || '';
    const hasOwner = gapOwner?.trim() || '';
    const hasRiskRanking = gapRiskRanking !== 'Unranked';
    const hasGapType = !!gapType;
    // For Open status, Management Prioritization must be selected (High/Med/Low/Unranked, not null)
    const hasManagementPrioritization = managementPrioritization !== null;
    
    // For Open status, Management Prioritization is required
    // For Draft/Create, it's optional (validation happens in handleSaveClick for Draft → Open transition)
    const isOpenStatus = isEdit && existingGap?.status === 'Open';
    
    return (
      hasDescription.length > 0 &&
      hasRiskDescription.length > 0 &&
      hasOwner.length > 0 &&
      hasRiskRanking &&
      hasGapType &&
      (!isOpenStatus || hasManagementPrioritization)
    );
  }, [gapDescription, riskDescription, gapOwner, gapRiskRanking, gapType, managementPrioritization, isEdit, existingGap?.status]);

  // Handle cycle selection
  const handleSelectCycle = (cycle: CycleItem) => {
    setSelectedCycle(cycle);
    setSelectedProcess(null); // Reset process when cycle changes
    setCycleDropdownOpen(false);
    // Open process dropdown after selecting cycle
    setTimeout(() => {
      setProcessDropdownOpen(true);
    }, 100);
  };

  // Handle process selection
  const handleSelectProcess = (process: ProcessItem) => {
    setSelectedProcess(process);
    setProcessDropdownOpen(false);
  };

  // Handle Risk Matrix open
  const handleOpenRiskMatrix = () => {
    setIsRiskMatrixModalOpen(true);
  };

  // Handle Risk Matrix selection
  const handleRiskMatrixSelect = (
    level: RiskRanking,
    severityIndex: number,
    likelihoodIndex: number
  ) => {
    setGapRiskRanking(level);
    setRiskRatingSeverityIndex(severityIndex);
    setRiskRatingLikelihoodIndex(likelihoodIndex);
    setIsRiskMatrixModalOpen(false);
  };

  // ==================== OBJECTIVES SEARCH MODE FUNCTIONS ====================

  // Enter objectives search mode
  const enterObjectivesSearchMode = () => {
    setObjectivesSearchMode(true);
    // Set default to current process
    if (selectedCycle) {
      setObjSearchCycle(selectedCycle);
    }
    if (selectedProcess) {
      setObjSearchProcess(selectedProcess);
      // Fetch objectives for current process
      fetchObjectivesForSearch(selectedProcess.riskAssessmentId);
    }
    // Copy current selections to temp
    setTempSelectedObjectives([...selectedObjectives]);
  };

  // Exit objectives search mode without saving
  const exitObjectivesSearchMode = () => {
    setObjectivesSearchMode(false);
    setObjSearchCycle(null);
    setObjSearchProcess(null);
    setObjSearchObjectives([]);
    setTempSelectedObjectives([]);
    setObjCycleSearchTerm('');
    setObjProcessSearchTerm('');
  };

  // Confirm objectives selection
  const confirmObjectivesSelection = () => {
    setSelectedObjectives(tempSelectedObjectives);
    exitObjectivesSearchMode();
  };

  // Fetch objectives for a given riskAssessmentId
  const fetchObjectivesForSearch = async (riskAssessmentId: string) => {
    if (!riskAssessmentId) return;

    setObjSearchLoading(true);
    try {
      const response = await getObjectives(riskAssessmentId, {
        skip: 0,
        take: 1000,
        requiresCounts: true,
      });
      const parsedObjectives = parseObjectives(response.result || []);
      setTempSelectedObjectives(prev => prev.map(obj => {
        const o = parsedObjectives.find((o) => o.id === obj.id);
        if (o) {
          return {
            ...obj,
            riskRanking: o.controlRiskRanking,
            assertions: o.financialStatementAssertions,
          };
        }
        return obj;
      }));
      setObjSearchObjectives(parsedObjectives);
    } catch (error) {
      console.error('Failed to fetch objectives for search:', error);
      message.error('Failed to load objectives');
      setObjSearchObjectives([]);
    } finally {
      setObjSearchLoading(false);
    }
  };

  // Handle cycle selection in objectives search
  const handleObjSearchCycleSelect = (cycle: CycleItem) => {
    setObjSearchCycle(cycle);
    setObjSearchProcess(null);
    setObjSearchObjectives([]);
    setObjCycleSearchTerm('');
    setObjCycleDropdownOpen(false);
    setObjSearchProcessResults([]);
    setTimeout(() => {
      setObjProcessDropdownOpen(true);
    }, 100);
  };

  // Handle process selection in objectives search
  const handleObjSearchProcessSelect = (process: ProcessItem) => {
    setObjSearchProcess(process);
    setObjProcessSearchTerm('');
    setObjProcessDropdownOpen(false);
    // Fetch objectives for selected process
    fetchObjectivesForSearch(process.riskAssessmentId);
  };

  // Toggle objective selection
  const toggleObjectiveSelection = (obj: Objective) => {
    const processCode = objSearchProcess?.processCode || '';
    const objectiveNumber = obj.index.toString().padStart(2, '0');

    const isSelected = tempSelectedObjectives.some(o => o.id === obj.id);

    if (isSelected) {
      setTempSelectedObjectives(prev => prev.filter(o => o.id !== obj.id));
    } else {
      setTempSelectedObjectives(prev => [...prev, {
        id: obj.id,
        processCode,
        objectiveNumber,
        riskRanking: obj.controlRiskRanking,
        assertions: obj.financialStatementAssertions,
      }]);
    }
  };

  // Check if objective is selected
  const isObjectiveSelected = (objId: string) => {
    return tempSelectedObjectives.some(o => o.id === objId);
  };

  // ==================== CONTROLS SEARCH MODE FUNCTIONS ====================

  // Enter controls search mode
  const enterControlsSearchMode = () => {
    setControlsSearchMode(true);
    // Set default to current process
    if (selectedCycle) {
      setCtrlSearchCycle(selectedCycle);
    }
    if (selectedProcess) {
      setCtrlSearchProcess(selectedProcess);
      // Fetch controls for current process
      fetchControlsForSearch(selectedProcess.processId);
    }
    // Copy current selections to temp
    setTempSelectedControls([...selectedControls]);
  };

  // Exit controls search mode without saving
  const exitControlsSearchMode = () => {
    setControlsSearchMode(false);
    setCtrlSearchCycle(null);
    setCtrlSearchProcess(null);
    setCtrlSearchControls([]);
    setTempSelectedControls([]);
    setCtrlCycleSearchTerm('');
    setCtrlProcessSearchTerm('');
  };

  // Confirm controls selection
  const confirmControlsSelection = () => {
    setSelectedControls(tempSelectedControls);
    exitControlsSearchMode();
  };

  // Fetch controls for a given processId
  const fetchControlsForSearch = async (processId: string) => {
    if (!processId || !selectedReport?.annualReportId) return;

    setCtrlSearchLoading(true);
    try {
      const response = await getControlActivities({
        annualReportId: selectedReport.annualReportId,
        processIds: [processId],
        sorted: [{name: "ControlCode", direction: "ascending"}],
      });
      setCtrlSearchControls(response.data);
    } catch (error) {
      console.error('Failed to fetch controls for search:', error);
      message.error('Failed to load controls');
      setCtrlSearchControls([]);
    } finally {
      setCtrlSearchLoading(false);
    }
  };

  // Handle cycle selection in controls search
  const handleCtrlSearchCycleSelect = (cycle: CycleItem) => {
    setCtrlSearchCycle(cycle);
    setCtrlSearchProcess(null);
    setCtrlSearchControls([]);
    setCtrlCycleSearchTerm('');
    setCtrlCycleDropdownOpen(false);
    setCtrlSearchProcessResults([]);
    setTimeout(() => {
      setCtrlProcessDropdownOpen(true);
    }, 100);
  };

  // Handle process selection in controls search
  const handleCtrlSearchProcessSelect = (process: ProcessItem) => {
    setCtrlSearchProcess(process);
    setCtrlProcessSearchTerm('');
    setCtrlProcessDropdownOpen(false);
    // Fetch controls for selected process
    fetchControlsForSearch(process.processId);
  };

  // Toggle control selection
  const toggleControlSelection = (ctrl: ControlActivity) => {
    const isSelected = tempSelectedControls.some(c => c.id === ctrl.id);

    if (isSelected) {
      setTempSelectedControls(prev => prev.filter(c => c.id !== ctrl.id));
    } else {
      setTempSelectedControls(prev => [...prev, {
        id: ctrl.id,
        controlCode: ctrl.controlCode,
      }]);
    }
  };

  // Check if control is selected
  const isControlSelected = (ctrlId: string) => {
    return tempSelectedControls.some(c => c.id === ctrlId);
  };

  // Template for Financial Statement Assertions column
  const assertionsTemplate = (props: Objective) => {
    if (!props.financialStatementAssertions || props.financialStatementAssertions.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    // Tooltip content for multiple assertions
    const tooltipContent = props.financialStatementAssertions.length > 1 ? (
      <div>
        <TooltipTitle>Financial Statement Assertions</TooltipTitle>
        <TooltipList>
          {props.financialStatementAssertions.map((assertion, index) => (
            <TooltipItem key={index}>
              {assertion}
            </TooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const assertionsContent = (
      <div>
        {props.financialStatementAssertions.map((assertion, index) => (
          <AssertionChip key={index}>{assertion}</AssertionChip>
        ))}
      </div>
    );

    // Show tooltip only if multiple assertions
    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {assertionsContent}
        </Tooltip>
      );
    }

    return assertionsContent;
  };

  // Reload gaps data
  const reloadData = async () => {
    if (!selectedReport?.annualReportId) return [];

    try {
      const response = await getAllGaps({
        annualReportId: selectedReport.annualReportId,
      });
      dispatch(setGaps({
        data: response.data,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId
      }));
      return response.data ?? [];
    } catch (error) {
      console.error('Error refreshing gaps:', error);
      return [];
    }
  };

  // Handle Save as Draft
  // Custom tag render for Applications Select
  const renderApplicationTag = (props: CustomTagProps) => {
    const { label, closable, onClose } = props;
    
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <ApplicationSelectTag onMouseDown={onPreventMouseDown}>
        {label}
        {closable && (
          <ApplicationCloseIcon
            $circleFill="#E5E7EB"
            $strokeColor="#6B7280"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          />
        )}
      </ApplicationSelectTag>
    );
  };

  const handleSaveAsDraft = async () => {
    if (!selectedCycle || !selectedProcess) {
      message.error('Please select Cycle and Process');
      return;
    }

    setIsSavingAsDraft(true);
    const formValues = form.getFieldsValue();
    const baseRequest: CreateGapRequest = {
      status: mapStatusToApi('Draft'),
      description: formValues.gapDescription || '',
      riskDescription: formValues.riskDescription || '',
      riskRating: mapRiskRankingToApi(gapRiskRanking),
      riskRatingSeverityIndex: riskRatingSeverityIndex ?? null,
      riskRatingLikelihoodIndex: riskRatingLikelihoodIndex ?? null,
      type: mapGapTypeToApi(gapType),
      owner: formValues.gapOwner || '',
      processId: selectedProcess.processId,
      controlIds: selectedControls.map(c => c.id),
      objectiveIds: selectedObjectives.map(o => o.id),
      applicationIds: selectedApplicationIds,
      managementPrioritization: mapPriorityToApi(managementPrioritization),
      remediationRecommendation: formValues.remediationRecommendation || '',
      managementResponse: formValues.managementResponse || '',
      dateIdentified: formValues.dateIdentified?.toISOString() || null,
      gapClosureDate: null,
    };
    try {
      if (isEdit && gapId) {
        // Update existing gap
        const request: UpdateGapRequest = {
          gapId,
          ...baseRequest,
        };
        await updateGap(request);
        await reloadData();
        // Clear hierarchy cache so EX3 gap counts refresh
        dispatch(clearHierarchyData());
        message.success('Draft saved successfully');
      } else {
        // Create new gap
        await createGap(baseRequest);

        const reloadedData = await reloadData();
        // Clear hierarchy cache so EX3 gap counts refresh
        dispatch(clearHierarchyData());
        message.success('Draft saved successfully');

        navigateToNewlyCreatedGap(reloadedData);
      }
    } catch (error: any) {
      console.error('Failed to save draft:', error);
      message.error(error.message || 'Failed to save draft');
    } finally {
      setIsSavingAsDraft(false);
    }
  };

  // Handle Save button click - may show confirmation modal for Draft -> Open
  const handleSaveClick = () => {
    if (!selectedCycle || !selectedProcess) {
      message.error('Please select Cycle and Process');
      return;
    }

    // Validate required fields
    const formValues = form.getFieldsValue();
    
    if (!formValues.gapDescription?.trim()) {
      message.error('Please enter GAP Description');
      return;
    }
    if (!formValues.riskDescription?.trim()) {
      message.error('Please enter GAP Risk Description');
      return;
    }
    if (gapRiskRanking === 'Unranked') {
      message.error('Please select GAP Risk Ranking');
      return;
    }
    if (!gapType) {
      message.error('Please select GAP Type');
      return;
    }
    if (!formValues.gapOwner?.trim()) {
      message.error('Please enter GAP Owner');
      return;
    }
    // Management Prioritization must be selected (High/Med/Low/Unranked)
    if (managementPrioritization === null) {
      message.error('Please select Management Prioritization');
      return;
    }

    // For Draft status (create new or edit draft), show confirmation modal
    const isDraft = !isEdit || existingGap?.status === 'Draft';
    if (isDraft) {
      setOpenGapModalOpen(true);
    } else {
      // For Open status, save directly
      handleSaveAsActive();
    }
  };

  // Handle Archive button click - show confirmation modal
  const handleArchiveClick = () => {
    setArchiveGapModalOpen(true);
  };

  // Handle confirm Open Gap from modal
  const handleConfirmOpenGap = () => {
    setOpenGapModalOpen(false);
    handleSaveAsActive();
  };

  // Handle confirm Archive Gap from modal
  const handleConfirmArchive = () => {
    setArchiveGapModalOpen(false);
    handleArchive();
  };

  // Handle Save (Activate)
  const handleSaveAsActive = async () => {
    if (!selectedCycle || !selectedProcess) {
      message.error('Please select Cycle and Process');
      return;
    }

    // Validate required fields for Draft -> Open transition
    const isArchived = isEdit && existingGap?.status === 'Archived';
    if (!isArchived) {
      if (gapRiskRanking === 'Unranked') {
        message.error('Please select GAP Risk Ranking');
        return;
      }
    }

    setIsSavingAsActive(true);
    const formValues = form.getFieldsValue();

    // Determine status: keep Archived if already archived, otherwise Open
    const targetStatus = isArchived ? 'Archived' : 'Open';

    // For archived gaps, preserve/update gapClosureDate; for others, set to null
    const closureDate = isArchived
      ? (formValues.gapClosureDate?.toISOString() || existingGap?.gapClosureDate || null)
      : null;

    const baseRequest: CreateGapRequest = {
      status: mapStatusToApi(targetStatus),
      description: formValues.gapDescription || '',
      riskDescription: formValues.riskDescription || '',
      riskRating: mapRiskRankingToApi(gapRiskRanking),
      riskRatingSeverityIndex: riskRatingSeverityIndex ?? null,
      riskRatingLikelihoodIndex: riskRatingLikelihoodIndex ?? null,
      type: mapGapTypeToApi(gapType),
      owner: formValues.gapOwner || '',
      processId: selectedProcess.processId,
      controlIds: selectedControls.map(c => c.id),
      objectiveIds: selectedObjectives.map(o => o.id),
      applicationIds: selectedApplicationIds,
      managementPrioritization: mapPriorityToApi(managementPrioritization),
      remediationRecommendation: formValues.remediationRecommendation || '',
      managementResponse: formValues.managementResponse || '',
      dateIdentified: formValues.dateIdentified?.toISOString() || null,
      gapClosureDate: closureDate,
    };
    try {
      if (isEdit && gapId) {
        // Update existing gap
        const request: UpdateGapRequest = {
          gapId,
          ...baseRequest,
        };
        await updateGap(request);
        await reloadData();
        // Clear hierarchy cache so EX3 gap counts refresh
        dispatch(clearHierarchyData());
        message.success('Gap saved successfully');
      } else {
        // Create new gap
        await createGap(baseRequest);
        const reloadedData = await reloadData();
        // Clear hierarchy cache so EX3 gap counts refresh
        dispatch(clearHierarchyData());
        message.success('Gap created successfully');

        navigateToNewlyCreatedGap(reloadedData);
      }
    } catch (error: any) {
      console.error('Failed to save gap:', error);
      message.error(error.message || 'Failed to save gap');
    } finally {
      setIsSavingAsActive(false);
    }
  };

  const navigateToNewlyCreatedGap = (gaps: Gap[]) => {
    const newlyCreatedGap = gaps.reduce((latest, current) => {
      if (!latest) return current;
      const latestDate = new Date(latest.createdAt).getTime();
      const currentDate = new Date(current.createdAt).getTime();
      return currentDate > latestDate ? current : latest;
    }, gaps[0]);
    const newGapId = newlyCreatedGap?.id;
    if (newGapId) {
      navigate(`/gaps/${newGapId}`, { replace: true });
    }
  };

  // Handle Archive (Open -> Archived)
  const handleArchive = async () => {
    if (!selectedCycle || !selectedProcess || !gapId) {
      message.error('Cannot archive this gap');
      return;
    }

    setIsArchiving(true);
    const formValues = form.getFieldsValue();
    
    // Use today's date for gapClosureDate if not already set
    const closureDate = formValues.gapClosureDate
      ? formValues.gapClosureDate.toISOString()
      : new Date().toISOString();

    const request: UpdateGapRequest = {
      gapId,
      status: mapStatusToApi('Archived'),
      description: formValues.gapDescription || '',
      riskDescription: formValues.riskDescription || '',
      riskRating: mapRiskRankingToApi(gapRiskRanking),
      riskRatingSeverityIndex: riskRatingSeverityIndex ?? null,
      riskRatingLikelihoodIndex: riskRatingLikelihoodIndex ?? null,
      type: mapGapTypeToApi(gapType),
      owner: formValues.gapOwner || '',
      processId: selectedProcess.processId,
      controlIds: selectedControls.map(c => c.id),
      objectiveIds: selectedObjectives.map(o => o.id),
      applicationIds: selectedApplicationIds,
      managementPrioritization: mapPriorityToApi(managementPrioritization),
      remediationRecommendation: formValues.remediationRecommendation || '',
      managementResponse: formValues.managementResponse || '',
      dateIdentified: formValues.dateIdentified?.toISOString() || null,
      gapClosureDate: closureDate,
    };

    try {
      await updateGap(request);
      await reloadData();
      // Clear hierarchy cache so EX3 gap counts refresh
      dispatch(clearHierarchyData());
      message.success('Gap archived successfully');
    } catch (error: any) {
      console.error('Failed to archive gap:', error);
      message.error(error.message || 'Failed to archive gap');
    } finally {
      setIsArchiving(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/gaps');
  };

  // Cycle dropdown content
  const cycleDropdownContent = (
    <DropdownMenu>
      <SearchInput
        placeholder="Search cycles..."
        value={cycleSearchQuery}
        onChange={(e) => setCycleSearchQuery(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
      <DropdownSection>
        <DropdownSectionLabel>Select a Cycle</DropdownSectionLabel>
        {isLoadingCycles ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <LoadingOutlined style={{ color: '#2563EB' }} />
          </div>
        ) : filteredCycles.length === 0 ? (
          <div style={{ padding: '12px', textAlign: 'center', color: '#6B7280' }}>
            No cycles found
          </div>
        ) : (
          filteredCycles.map((cycle) => (
            <CycleDropdownItem
              key={cycle.cycleId}
              $selected={selectedCycle?.cycleId === cycle.cycleId}
              onClick={() => handleSelectCycle(cycle)}
            >
              <CycleDropdownItemText>
                {cycle.cycleCode} {cycle.cycleName}
              </CycleDropdownItemText>
              <CycleDropdownItemIcon>
                <ChevronRightIcon />
              </CycleDropdownItemIcon>
            </CycleDropdownItem>
          ))
        )}
      </DropdownSection>
    </DropdownMenu>
  );

  // Process dropdown content
  const processDropdownContent = (
    <DropdownMenu>
      <SearchInput
        placeholder="Search processes..."
        value={processSearchQuery}
        onChange={(e) => setProcessSearchQuery(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
      <DropdownSection>
        <DropdownSectionLabel>Select a Process</DropdownSectionLabel>
        {isLoadingProcesses ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <LoadingOutlined style={{ color: '#2563EB' }} />
          </div>
        ) : filteredProcesses.length === 0 ? (
          <div style={{ padding: '12px', textAlign: 'center', color: '#6B7280' }}>
            {selectedCycle ? 'No processes found' : 'Select a cycle first'}
          </div>
        ) : (
          filteredProcesses.map((process) => (
            <ProcessDropdownItem
              key={process.processId}
              onClick={() => handleSelectProcess(process)}
            >
              <ProcessDropdownItemChip $selected={selectedProcess?.processId === process.processId}>
                <ProcessDropdownItemText $selected={selectedProcess?.processId === process.processId}>
                  {process.processCode} {process.processName}
                </ProcessDropdownItemText>
              </ProcessDropdownItemChip>
            </ProcessDropdownItem>
          ))
        )}
      </DropdownSection>
    </DropdownMenu>
  );

  // Loading state
  if (isLoadingGap) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>ICFR GAP Tracker</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading gap data...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  // Gap not found
  if (gapNotFound) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>ICFR GAP Tracker</PageTitle>
        </PageHeader>
        <EmptyStateContainer>
          <EmptyStateText>Gap not found</EmptyStateText>
          <EmptyStateSubtext>The gap you're looking for doesn't exist or has been deleted.</EmptyStateSubtext>
          <Button type="primary" onClick={handleBack}>Back to Gaps</Button>
        </EmptyStateContainer>
      </PageContainer>
    );
  }

  // Show empty state for create mode when no selection
  const showEmptyState = !isEdit && (!selectedCycle || !selectedProcess);
  // Show form when we have both cycle and process selected
  const showForm = selectedCycle && selectedProcess;

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle level={2}>ICFR GAP Tracker</PageTitle>

        {/* Selectors Section - always show, disabled in edit mode */}
        {!showForm && (
          <SelectorsSection>
            {/* Cycle Selector Row */}
            <SelectorRow>
              <Dropdown
                open={cycleDropdownOpen}
                onOpenChange={setCycleDropdownOpen}
                dropdownRender={() => cycleDropdownContent}
                trigger={isEdit ? [] : ['click']}
                placement="bottomLeft"
              >
                <CycleSelector
                  onClick={() => !isEdit && setCycleDropdownOpen(!cycleDropdownOpen)}
                  style={{ opacity: isEdit ? 0.6 : 1, cursor: isEdit ? 'default' : 'pointer' }}
                >
                  <CycleText>
                    {selectedCycle ? `${selectedCycle.cycleCode} ${selectedCycle.cycleName}` : 'Select Cycle'}
                  </CycleText>
                  <ChevronIcon $color="#1F2937">
                    <ChevronDownIcon />
                  </ChevronIcon>
                </CycleSelector>
              </Dropdown>
            </SelectorRow>

            {/* Process Selector Row */}
            <SelectorRow>
              <Dropdown
                open={processDropdownOpen}
                onOpenChange={(open) => selectedCycle && !isEdit && setProcessDropdownOpen(open)}
                dropdownRender={() => processDropdownContent}
                trigger={selectedCycle && !isEdit ? ['click'] : []}
                placement="bottomLeft"
              >
                <ProcessSelectorsWrapper
                  $disabled={!selectedCycle || isEdit}
                  onClick={() => selectedCycle && !isEdit && setProcessDropdownOpen(!processDropdownOpen)}
                >
                  {selectedProcess ? (
                    <ProcessChipDisplay>
                      {selectedProcess.processCode} {selectedProcess.processName}
                    </ProcessChipDisplay>
                  ) : (
                    <span style={{
                      fontFamily: 'Inter',
                      fontSize: '16px',
                      color: '#9CA3AF',
                      fontWeight: 500
                    }}>
                      {selectedCycle ? 'Select Process' : 'Select a cycle first'}
                    </span>
                  )}
                  {selectedCycle && (
                    <ChevronIcon $color="#6B7280">
                      <ChevronDownIcon />
                    </ChevronIcon>
                  )}
                </ProcessSelectorsWrapper>
              </Dropdown>
            </SelectorRow>
          </SelectorsSection>
        )}
      </PageHeader>

      {/* Empty state when no selection */}
      {showEmptyState && (
        <EmptyStateContainer>
          <EmptyStateText>
            {!selectedCycle
              ? 'Select a Cycle to begin creating a Gap'
              : 'Select a Process to continue'
            }
          </EmptyStateText>
          <EmptyStateSubtext>
            {!selectedCycle
              ? 'Gaps must be associated with a specific Cycle and Process.'
              : 'Choose a process within the selected cycle.'
            }
          </EmptyStateSubtext>
        </EmptyStateContainer>
      )}

      {/* Form Content */}
      {showForm && !objectivesSearchMode && !controlsSearchMode && (
        <>
          <HeaderTop>
            <BackButton onClick={handleBack}>
              <LeftOutlined />
            </BackButton>
            <PageTitleSection>
              <PageTitleWrapper>
                <GapIdTitle>
                  {isEdit
                    ? (existingGap?.status === 'Draft' || !existingGap?.gapId
                      ? 'Draft Gap (Pending ID)'
                      : existingGap.gapId)
                    : 'New Gap (Pending ID)'}
                </GapIdTitle>
              </PageTitleWrapper>
              {selectedProcess && (
                <ProcessOwnerChip>
                  <ProcessOwnerAvatar>
                    {selectedProcess.processOwnerName?.charAt(0) || 'P'}
                  </ProcessOwnerAvatar>
                  {selectedProcess.processOwnerName || 'Process Owner'}
                </ProcessOwnerChip>
              )}
            </PageTitleSection>
          </HeaderTop>

          <ContentWrapper>
            {/* Identity Section */}
            <Section>
              <SectionTitle>Identity</SectionTitle>
              <IdentitySection>
                <IdentityRow>
                  <IdentityLabel>Status:</IdentityLabel>
                  <StatusChip $status={status}>{status}</StatusChip>
                </IdentityRow>
                <IdentityRow>
                  <IdentityLabel>Cycle:</IdentityLabel>
                  <CycleChip>{selectedCycle.cycleCode}</CycleChip>
                </IdentityRow>
                <IdentityRow>
                  <IdentityLabel>Process:</IdentityLabel>
                  <ProcessChip>{selectedProcess.processCode}</ProcessChip>
                </IdentityRow>
                <IdentityRow>
                  <IdentityLabel>GAP ID:</IdentityLabel>
                  <GapIdChip>
                    {isEdit
                      ? (existingGap?.status === 'Draft' || !existingGap?.gapId
                        ? 'Pending'
                        : existingGap.gapId)
                      : 'Pending'}
                  </GapIdChip>
                </IdentityRow>
              </IdentitySection>
            </Section>

            {/* Form Fields */}
            <Form form={form} layout="vertical" initialValues={{
              dateIdentified: dayjs(),
            }}>
              <Section>
                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    GAP Description {!isArchived && <RequiredMark>*</RequiredMark>}
                  </FieldLabel>
                  <Form.Item name="gapDescription" noStyle>
                    <StyledTextArea
                      placeholder="Describe the gap/deficiency..."
                      rows={2}
                      disabled={isArchived}
                      spellCheck={true}
                    />
                  </Form.Item>
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    GAP Risk Description {!isArchived && <RequiredMark>*</RequiredMark>}
                  </FieldLabel>
                  <Form.Item name="riskDescription" noStyle>
                    <StyledTextArea
                      placeholder="Describe the risk associated with this gap..."
                      rows={2}
                      disabled={isArchived}
                      spellCheck={true}
                    />
                  </Form.Item>
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    GAP Risk Ranking {!isArchived && <RequiredMark>*</RequiredMark>}
                  </FieldLabel>
                  <div>
                    <RiskRankingChip
                      $level={gapRiskRanking}
                      onClick={isArchived ? undefined : handleOpenRiskMatrix}
                      style={{ cursor: isArchived ? 'default' : 'pointer', opacity: isArchived ? 0.7 : 1 }}
                    >
                      {gapRiskRanking}
                    </RiskRankingChip>
                    {!isArchived && <RiskChipHelper>Click to open Risk Matrix</RiskChipHelper>}
                  </div>
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    GAP Type {!isArchived && <RequiredMark>*</RequiredMark>}
                  </FieldLabel>
                  <StyledSelect
                    value={gapType}
                    placeholder="Select GAP type"
                    onChange={(value) => setGapType(value as GapType | null)}
                    options={[
                      { label: 'Design', value: 'Design' },
                      { label: 'Effectiveness', value: 'Effectiveness' },
                      { label: 'Other', value: 'Other' },
                    ]}
                    disabled={isArchived}
                  />
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    GAP Owner {!isArchived && <RequiredMark>*</RequiredMark>}
                  </FieldLabel>
                  <Form.Item name="gapOwner" noStyle>
                    <StyledInput placeholder="Enter GAP owner name..." disabled={isArchived} spellCheck={true} />
                  </Form.Item>
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled>
                    Process Owner <ReadOnlyBadge>Read-only</ReadOnlyBadge>
                  </FieldLabel>
                  <StyledInput
                    value={selectedProcess?.processOwnerName || ''}
                    disabled
                  />
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>Related Objectives</FieldLabel>
                  <ObjectivesFieldWrapper 
                    onClick={isArchived ? undefined : enterObjectivesSearchMode}
                    style={{ cursor: isArchived ? 'default' : 'pointer', opacity: isArchived ? 0.7 : 1 }}
                  >
                    {selectedObjectives.length > 0 ? (
                      <>
                        {Object.entries(
                          selectedObjectives.reduce((acc, obj) => {
                            if (!acc[obj.processCode]) {
                              acc[obj.processCode] = [];
                            }
                            acc[obj.processCode].push(obj);
                            return acc;
                          }, {} as Record<string, SelectedObjective[]>)
                        ).map(([processCode, objectives]) => (
                          <SelectedObjectiveTag key={processCode}>
                            <ObjectiveProcessCode>{processCode}</ObjectiveProcessCode>
                            <ObjectiveNumbersGroup>
                              {objectives.map((obj) => (
                                <ObjectiveNumberCircle key={obj.id}>
                                  {obj.objectiveNumber}
                                </ObjectiveNumberCircle>
                              ))}
                            </ObjectiveNumbersGroup>
                          </SelectedObjectiveTag>
                        ))}
                      </>
                    ) : (
                      <span style={{ color: '#9CA3AF', fontFamily: 'Inter', fontSize: 14 }}>
                        {isArchived ? '-' : 'Select objectives...'}
                      </span>
                    )}
                  </ObjectivesFieldWrapper>
                  {!isArchived && (
                    <ObjectivesExpandIcon onClick={enterObjectivesSearchMode}>
                      <RightOutlined />
                    </ObjectivesExpandIcon>
                  )}
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>Compensating Controls</FieldLabel>
                  <ControlsFieldWrapper 
                    onClick={isArchived ? undefined : enterControlsSearchMode}
                    style={{ cursor: isArchived ? 'default' : 'pointer', opacity: isArchived ? 0.7 : 1 }}
                  >
                    {selectedControls.length > 0 ? (
                      <>
                        {selectedControls.map((ctrl) => (
                          <SelectedControlTag key={ctrl.id}>
                            {ctrl.controlCode}
                          </SelectedControlTag>
                        ))}
                      </>
                    ) : (
                      <span style={{ color: '#9CA3AF', fontFamily: 'Inter', fontSize: 14 }}>
                        {isArchived ? '-' : 'Select controls...'}
                      </span>
                    )}
                  </ControlsFieldWrapper>
                  {!isArchived && (
                    <ControlsExpandIcon onClick={enterControlsSearchMode}>
                      <RightOutlined />
                    </ControlsExpandIcon>
                  )}
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>Applications</FieldLabel>
                  <StyledSelect
                    mode="multiple"
                    value={selectedApplicationIds}
                    onChange={(value) => setSelectedApplicationIds(value as string[])}
                    placeholder={isLoadingApplications ? "Loading applications..." : "Select applications..."}
                    loading={isLoadingApplications}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    tagRender={renderApplicationTag}
                    options={applications.map(app => ({
                      value: app.id,
                      label: app.name,
                    }))}
                    notFoundContent={applications.length === 0 ? 'No applications available' : 'Not found'}
                    disabled={isArchived}
                  />
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    Management Prioritization {!isArchived && <RequiredMark>*</RequiredMark>}
                  </FieldLabel>
                  <StyledSelect
                    value={managementPrioritization}
                    placeholder="Select priority level"
                    onChange={(value) => setManagementPrioritization(value as PriorityLevel | null)}
                    disabled={isArchived}
                  >
                    <Select.Option value="High">High</Select.Option>
                    <Select.Option value="Med">Med</Select.Option>
                    <Select.Option value="Low">Low</Select.Option>
                    <Select.Option value="Unranked">Unranked</Select.Option>
                  </StyledSelect>
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    Remediation Recommendation
                  </FieldLabel>
                  <Form.Item name="remediationRecommendation" noStyle>
                    <StyledTextArea
                      placeholder="Enter remediation recommendation..."
                      rows={2}
                      disabled={isArchived}
                      spellCheck={true}
                    />
                  </Form.Item>
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    Management's Response
                  </FieldLabel>
                  <Form.Item name="managementResponse" noStyle>
                    <StyledTextArea
                      placeholder="Enter management's response..."
                      rows={2}
                      disabled={isArchived}
                      spellCheck={true}
                    />
                  </Form.Item>
                </FieldRow>

                <FieldRow>
                  <FieldLabel $isDisabled={isArchived}>
                    Date Identified
                  </FieldLabel>
                  <Form.Item name="dateIdentified" noStyle>
                    <StyledDatePicker
                      format="MM/DD/YYYY"
                      allowClear={false}
                      disabled={isArchived}
                    />
                  </Form.Item>
                </FieldRow>

                {/* GAP Closure Date - only visible when status is Archived */}
                {isArchived && (
                  <FieldRow>
                    <FieldLabel $isDisabled>
                      GAP Closure Date
                    </FieldLabel>
                    <Form.Item name="gapClosureDate" noStyle>
                      <StyledDatePicker
                        format="MM/DD/YYYY"
                        allowClear={false}
                        disabled
                      />
                    </Form.Item>
                  </FieldRow>
                )}
              </Section>
            </Form>
          </ContentWrapper>

          {/* Footer */}
          <Footer>
            <ButtonGroup>
              {isArchived ? (
                // Archived: Read-only mode, no action buttons
                <span style={{ 
                  fontFamily: 'Inter', 
                  fontSize: '14px', 
                  color: '#9CA3AF',
                  padding: '8px 16px',
                  background: '#F3F4F6',
                  borderRadius: '6px'
                }}>
                  Save
                </span>
              ) : isEdit && existingGap?.status === 'Open' ? (
                // Edit Open: Save + Archive (Archive shows confirmation modal)
                <>
                  <StyledOutlinedButton
                    type="default"
                    onClick={handleArchiveClick}
                    loading={isArchiving}
                    disabled={isSavingAsActive || !isAdmin}
                  >
                    Archive
                  </StyledOutlinedButton>
                  <StyledFilledButton
                    type="primary"
                    onClick={handleSaveAsActive}
                    loading={isSavingAsActive}
                    disabled={!isAllRequiredFieldsFilled || isArchiving || !isAdmin}
                  >
                    Save
                  </StyledFilledButton>
                </>
              ) : (
                // Add mode or Edit Draft: Save as Draft + Save (Save shows confirmation modal)
                <>
                  <StyledOutlinedButton
                    type="default"
                    onClick={handleSaveAsDraft}
                    loading={isSavingAsDraft}
                    disabled={isSavingAsActive || !isAdmin}
                  >
                    Save as Draft
                  </StyledOutlinedButton>
                  <StyledFilledButton
                    type="primary"
                    onClick={handleSaveClick}
                    loading={isSavingAsActive}
                    disabled={!isAllRequiredFieldsFilled || !isAdmin}
                  >
                    Save
                  </StyledFilledButton>
                </>
              )}
            </ButtonGroup>
          </Footer>
        </>
      )}

      {/* Objectives Search Mode */}
      {showForm && objectivesSearchMode && (
        <>
          <HeaderTop>
            <BackButton onClick={exitObjectivesSearchMode}>
              <LeftOutlined />
            </BackButton>
            <PageTitleSection>
              <PageTitleWrapper>
                <GapIdTitle>Select Objectives</GapIdTitle>
              </PageTitleWrapper>
            </PageTitleSection>
          </HeaderTop>

          <ContentWrapper>
            <ObjectivesSearchContainer>
              <ObjectivesSearchHeader>
                {/* Cycle Selector */}
                <SelectorsSection>
                  <SelectorRow>
                    <Dropdown
                      dropdownRender={() => (
                        <DropdownMenu onClick={(e) => e.stopPropagation()}>
                          <SearchInput
                            placeholder="Search for cycle"
                            value={objCycleSearchTerm}
                            onChange={(e) => setObjCycleSearchTerm(e.target.value)}
                            autoFocus
                          />
                          <DropdownSection>
                            {objCycleSearchTerm === '' && (
                              <DropdownSectionLabel>All cycles</DropdownSectionLabel>
                            )}
                            {objSearchCycleResults.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF' }}>
                                {isSearchingCycles ? 'Searching cycles...' : 'No cycles found'}
                              </div>
                            ) : (
                              objSearchCycleResults.map(cycle => (
                                <CycleDropdownItem
                                  key={cycle.cycleId}
                                  $selected={objSearchCycle?.cycleId === cycle.cycleId}
                                  onClick={() => handleObjSearchCycleSelect(cycle)}
                                >
                                  <CycleDropdownItemText>
                                    {cycle.cycleCode} {cycle.cycleName}
                                  </CycleDropdownItemText>
                                  <CycleDropdownItemIcon>
                                    <ChevronRightIcon />
                                  </CycleDropdownItemIcon>
                                </CycleDropdownItem>
                              ))
                            )}
                          </DropdownSection>
                        </DropdownMenu>
                      )}
                      trigger={['click']}
                      placement="bottomLeft"
                      open={objCycleDropdownOpen}
                      onOpenChange={setObjCycleDropdownOpen}
                    >
                      <CycleSelector>
                        <CycleText>
                          {objSearchCycle ? `Cycle: ${objSearchCycle.cycleCode} ${objSearchCycle.cycleName}` : 'Select Cycle'}
                        </CycleText>
                        <ChevronIcon $color="#1F2937">
                          <ChevronDownIcon />
                        </ChevronIcon>
                      </CycleSelector>
                    </Dropdown>
                  </SelectorRow>

                  {/* Process Selector */}
                  <SelectorRow>
                    <Dropdown
                      dropdownRender={() => (
                        <DropdownMenu onClick={(e) => e.stopPropagation()}>
                          <SearchInput
                            placeholder="Search for process"
                            value={objProcessSearchTerm}
                            onChange={(e) => setObjProcessSearchTerm(e.target.value)}
                            autoFocus
                          />
                          <DropdownSection>
                            {objProcessSearchTerm === '' && (
                              <DropdownSectionLabel>All processes</DropdownSectionLabel>
                            )}
                            {objSearchProcessResults.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF' }}>
                                {!objSearchCycle ? 'Select a cycle first' : isSearchingProcesses ? 'Searching processes...' : 'No processes found'}
                              </div>
                            ) : (
                              objSearchProcessResults.map(process => (
                                <ProcessDropdownItem
                                  key={process.processId}
                                  onClick={() => handleObjSearchProcessSelect(process)}
                                >
                                  <ProcessDropdownItemChip $selected={objSearchProcess?.processId === process.processId}>
                                    <ProcessDropdownItemText $selected={objSearchProcess?.processId === process.processId}>
                                      {process.processCode} {process.processName}
                                    </ProcessDropdownItemText>
                                  </ProcessDropdownItemChip>
                                </ProcessDropdownItem>
                              ))
                            )}
                          </DropdownSection>
                        </DropdownMenu>
                      )}
                      trigger={objSearchCycle ? ['click'] : []}
                      placement="bottomLeft"
                      disabled={!objSearchCycle}
                      open={objProcessDropdownOpen}
                      onOpenChange={(open) => objSearchCycle && setObjProcessDropdownOpen(open)}
                    >
                      <ProcessSelectorsWrapper $disabled={!objSearchCycle}>
                        {objSearchProcess ? (
                          <ProcessChipDisplay>
                            {objSearchProcess.processCode} {objSearchProcess.processName}
                          </ProcessChipDisplay>
                        ) : (
                          <span style={{ color: '#9CA3AF', fontFamily: 'Inter', fontSize: 14 }}>
                            {objSearchCycle ? 'Select Process' : 'Select a cycle first'}
                          </span>
                        )}
                        {objSearchCycle && (
                          <ChevronIcon $color="#6B7280">
                            <ChevronDownIcon />
                          </ChevronIcon>
                        )}
                      </ProcessSelectorsWrapper>
                    </Dropdown>
                  </SelectorRow>
                </SelectorsSection>
              </ObjectivesSearchHeader>

              {/* Objectives Grid */}
              {objSearchLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                  <LoadingOutlined style={{ fontSize: 32, color: '#2563EB' }} />
                </div>
              ) : objSearchObjectives.length === 0 ? (
                <EmptyStateContainer>
                  <EmptyStateText>
                    {!objSearchProcess ? 'Select a process to view objectives' : 'No objectives found for this process'}
                  </EmptyStateText>
                </EmptyStateContainer>
              ) : (
                <ObjectivesGridWrapper>
                  <GridComponent
                    key={`objectives-search-grid-${objSearchObjectives.length}`}
                    dataSource={objSearchObjectives}
                    allowPaging={false}
                    enableHover={true}
                  >
                    <ColumnsDirective>
                      <ColumnDirective
                        headerText=''
                        width='50'
                        textAlign='Center'
                        template={(obj: Objective) => (
                          <CheckboxCell>
                            <Checkbox
                              checked={isObjectiveSelected(obj.id)}
                              onChange={() => toggleObjectiveSelection(obj)}
                            />
                          </CheckboxCell>
                        )}
                      />
                      <ColumnDirective
                        field='index'
                        headerText='#'
                        width='100'
                        textAlign='Center'
                        template={(obj: Objective) => (
                          <IndexCell>
                            {objSearchProcess?.processCode.substring(0,3)}.{obj.index.toString().padStart(2, '0')}
                          </IndexCell>
                        )}
                      />
                      <ColumnDirective
                        field='objectives'
                        headerText='Objectives'
                        width='auto'
                        textAlign='Left'
                        template={(obj: Objective) => (
                          <CellContent dangerouslySetInnerHTML={{ __html: obj.objectives || '' }} />
                        )}
                      />
                      <ColumnDirective
                        field='risks'
                        headerText='Risks'
                        width='auto'
                        textAlign='Left'
                        template={(obj: Objective) => (
                          <CellContent dangerouslySetInnerHTML={{ __html: obj.risks || '' }} />
                        )}
                      />
                      <ColumnDirective
                        field='controlRiskRanking'
                        headerText='Control Risk Ranking'
                        width='200'
                        textAlign='Left'
                        template={(obj: Objective) => (
                          <RiskChip $risk={obj.controlRiskRanking}>
                            {obj.controlRiskRanking}
                          </RiskChip>
                        )}
                      />
                      <ColumnDirective
                        field='financialStatementAssertions'
                        headerText='Financial Statement Assertions'
                        width='240'
                        textAlign='Left'
                        template={assertionsTemplate}
                      />
                    </ColumnsDirective>
                    <Inject services={[Page]} />
                  </GridComponent>
                </ObjectivesGridWrapper>
              )}
            </ObjectivesSearchContainer>
          </ContentWrapper>

          <Footer>
            <ButtonGroup>
              <StyledOutlinedButton
                type="default"
                onClick={exitObjectivesSearchMode}
              >
                Cancel
              </StyledOutlinedButton>
              <StyledFilledButton
                type="primary"
                onClick={confirmObjectivesSelection}
              >
                Done ({tempSelectedObjectives.length} selected)
              </StyledFilledButton>
            </ButtonGroup>
          </Footer>
        </>
      )}

      {/* Controls Search Mode */}
      {showForm && controlsSearchMode && (
        <>
          <HeaderTop>
            <BackButton onClick={exitControlsSearchMode}>
              <LeftOutlined />
            </BackButton>
            <PageTitleSection>
              <PageTitleWrapper>
                <GapIdTitle>Select Controls</GapIdTitle>
              </PageTitleWrapper>
            </PageTitleSection>
          </HeaderTop>

          <ContentWrapper>
            <ObjectivesSearchContainer>
              <ObjectivesSearchHeader>
                {/* Cycle Selector */}
                <SelectorsSection>
                  <SelectorRow>
                    <Dropdown
                      dropdownRender={() => (
                        <DropdownMenu onClick={(e) => e.stopPropagation()}>
                          <SearchInput
                            placeholder="Search for cycle"
                            value={ctrlCycleSearchTerm}
                            onChange={(e) => setCtrlCycleSearchTerm(e.target.value)}
                            autoFocus
                          />
                          <DropdownSection>
                            {ctrlCycleSearchTerm === '' && (
                              <DropdownSectionLabel>All cycles</DropdownSectionLabel>
                            )}
                            {ctrlSearchCycleResults.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF' }}>
                                {isSearchingCtrlCycles ? 'Searching cycles...' : 'No cycles found'}
                              </div>
                            ) : (
                              ctrlSearchCycleResults.map(cycle => (
                                <CycleDropdownItem
                                  key={cycle.cycleId}
                                  $selected={ctrlSearchCycle?.cycleId === cycle.cycleId}
                                  onClick={() => handleCtrlSearchCycleSelect(cycle)}
                                >
                                  <CycleDropdownItemText>
                                    {cycle.cycleCode} {cycle.cycleName}
                                  </CycleDropdownItemText>
                                  <CycleDropdownItemIcon>
                                    <ChevronRightIcon />
                                  </CycleDropdownItemIcon>
                                </CycleDropdownItem>
                              ))
                            )}
                          </DropdownSection>
                        </DropdownMenu>
                      )}
                      trigger={['click']}
                      placement="bottomLeft"
                      open={ctrlCycleDropdownOpen}
                      onOpenChange={setCtrlCycleDropdownOpen}
                    >
                      <CycleSelector>
                        <CycleText>
                          {ctrlSearchCycle ? `Cycle: ${ctrlSearchCycle.cycleCode} ${ctrlSearchCycle.cycleName}` : 'Select Cycle'}
                        </CycleText>
                        <ChevronIcon $color="#1F2937">
                          <ChevronDownIcon />
                        </ChevronIcon>
                      </CycleSelector>
                    </Dropdown>
                  </SelectorRow>

                  {/* Process Selector */}
                  <SelectorRow>
                    <Dropdown
                      dropdownRender={() => (
                        <DropdownMenu onClick={(e) => e.stopPropagation()}>
                          <SearchInput
                            placeholder="Search for process"
                            value={ctrlProcessSearchTerm}
                            onChange={(e) => setCtrlProcessSearchTerm(e.target.value)}
                            autoFocus
                          />
                          <DropdownSection>
                            {ctrlProcessSearchTerm === '' && (
                              <DropdownSectionLabel>All processes</DropdownSectionLabel>
                            )}
                            {ctrlSearchProcessResults.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: 16, color: '#9CA3AF' }}>
                                {!ctrlSearchCycle ? 'Select a cycle first' : isSearchingCtrlProcesses ? 'Searching processes...' : 'No processes found'}
                              </div>
                            ) : (
                              ctrlSearchProcessResults.map(process => (
                                <ProcessDropdownItem
                                  key={process.processId}
                                  onClick={() => handleCtrlSearchProcessSelect(process)}
                                >
                                  <ProcessDropdownItemChip $selected={ctrlSearchProcess?.processId === process.processId}>
                                    <ProcessDropdownItemText $selected={ctrlSearchProcess?.processId === process.processId}>
                                      {process.processCode} {process.processName}
                                    </ProcessDropdownItemText>
                                  </ProcessDropdownItemChip>
                                </ProcessDropdownItem>
                              ))
                            )}
                          </DropdownSection>
                        </DropdownMenu>
                      )}
                      trigger={ctrlSearchCycle ? ['click'] : []}
                      placement="bottomLeft"
                      disabled={!ctrlSearchCycle}
                      open={ctrlProcessDropdownOpen}
                      onOpenChange={(open) => ctrlSearchCycle && setCtrlProcessDropdownOpen(open)}
                    >
                      <ProcessSelectorsWrapper $disabled={!ctrlSearchCycle}>
                        {ctrlSearchProcess ? (
                          <ProcessChipDisplay>
                            {ctrlSearchProcess.processCode} {ctrlSearchProcess.processName}
                          </ProcessChipDisplay>
                        ) : (
                          <span style={{ color: '#9CA3AF', fontFamily: 'Inter', fontSize: 14 }}>
                            {ctrlSearchCycle ? 'Select Process' : 'Select a cycle first'}
                          </span>
                        )}
                        {ctrlSearchCycle && (
                          <ChevronIcon $color="#6B7280">
                            <ChevronDownIcon />
                          </ChevronIcon>
                        )}
                      </ProcessSelectorsWrapper>
                    </Dropdown>
                  </SelectorRow>
                </SelectorsSection>
              </ObjectivesSearchHeader>

              {/* Controls Grid */}
              {ctrlSearchLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                  <LoadingOutlined style={{ fontSize: 32, color: '#2563EB' }} />
                </div>
              ) : ctrlSearchControls.length === 0 ? (
                <EmptyStateContainer>
                  <EmptyStateText>
                    {!ctrlSearchProcess ? 'Select a process to view controls' : 'No controls found for this process'}
                  </EmptyStateText>
                </EmptyStateContainer>
              ) : (
                <ObjectivesGridWrapper>
                  <GridComponent
                    key={`controls-search-grid-${ctrlSearchControls.length}`}
                    dataSource={ctrlSearchControls}
                    allowPaging={false}
                    enableHover={true}
                    allowTextWrap={true}
                    textWrapSettings={{ wrapMode: 'Content' }}
                  >
                    <ColumnsDirective>
                      <ColumnDirective
                        headerText=''
                        width='50'
                        textAlign='Center'
                        template={(ctrl: ControlActivity) => (
                          <CheckboxCell>
                            <Checkbox
                              checked={isControlSelected(ctrl.id)}
                              onChange={() => toggleControlSelection(ctrl)}
                            />
                          </CheckboxCell>
                        )}
                      />
                      <ColumnDirective
                        field='controlCode'
                        headerText='Control ID'
                        width='100'
                        textAlign='Center'
                        template={(ctrl: ControlActivity) => (
                          <IndexCell>
                            {ctrl.controlCode}
                          </IndexCell>
                        )}
                      />
                      <ColumnDirective
                        field='controlDescription.what'
                        headerText='Control Description'
                        width='250'
                        textAlign='Left'
                        template={(ctrl: ControlActivity) => (
                          <CellContent>{ctrl.controlDescription?.what || '-'}</CellContent>
                        )}
                      />
                      <ColumnDirective
                        field='controlCategory'
                        headerText='Category'
                        width='120'
                        textAlign='Left'
                        template={(ctrl: ControlActivity) => (
                          <GrayChip>{ctrl.controlCategory}</GrayChip>
                        )}
                      />
                      <ColumnDirective
                        field='controlType'
                        headerText='Type'
                        width='110'
                        textAlign='Left'
                        template={(ctrl: ControlActivity) => (
                          <GrayChip>{ctrl.controlType}</GrayChip>
                        )}
                      />
                      <ColumnDirective
                        field='frequency'
                        headerText='Frequency'
                        width='100'
                        textAlign='Left'
                        template={(ctrl: ControlActivity) => (
                          <GrayChip>{ctrl.frequency}</GrayChip>
                        )}
                      />
                      <ColumnDirective
                        field='keyOrNonKey'
                        headerText='Key/Non-Key'
                        width='100'
                        textAlign='Left'
                        template={(ctrl: ControlActivity) => (
                          <GrayChip>{ctrl.keyOrNonKey}</GrayChip>
                        )}
                      />
                      <ColumnDirective
                        field='riskRanking'
                        headerText='Risk Ranking'
                        width='110'
                        textAlign='Left'
                        template={(ctrl: ControlActivity) => (
                          <CtrlRiskRankingChip $level={ctrl.riskRanking}>
                            {ctrl.riskRanking}
                          </CtrlRiskRankingChip>
                        )}
                      />
                      {/* <ColumnDirective
                        field='controlOwner'
                        headerText='Control Owner'
                        width='120'
                        textAlign='Left'
                        template={(ctrl: ControlActivity) => (
                          <CtrlControlOwnerText>{ctrl.controlOwner || '-'}</CtrlControlOwnerText>
                        )}
                      />
                      <ColumnDirective
                        field='processOwner'
                        headerText='Process Owner'
                        width='140'
                        textAlign='Left'
                        template={(ctrl: ControlActivity) => (
                          ctrl.processOwner ? (
                            <CtrlProcessOwnerContainer>
                              <CtrlProcessOwnerAvatar>
                                {ctrl.processOwner.avatar || ctrl.processOwner.name?.charAt(0) || 'P'}
                              </CtrlProcessOwnerAvatar>
                              <CtrlProcessOwnerName>{ctrl.processOwner.name}</CtrlProcessOwnerName>
                            </CtrlProcessOwnerContainer>
                          ) : (
                            <span style={{ color: '#9CA3AF' }}>-</span>
                          )
                        )}
                      /> */}
                    </ColumnsDirective>
                    <Inject services={[Page]} />
                  </GridComponent>
                </ObjectivesGridWrapper>
              )}
            </ObjectivesSearchContainer>
          </ContentWrapper>

          <Footer>
            <ButtonGroup>
              <StyledOutlinedButton
                type="default"
                onClick={exitControlsSearchMode}
              >
                Cancel
              </StyledOutlinedButton>
              <StyledFilledButton
                type="primary"
                onClick={confirmControlsSelection}
              >
                Done ({tempSelectedControls.length} selected)
              </StyledFilledButton>
            </ButtonGroup>
          </Footer>
        </>
      )}

      {/* Risk Matrix Modal */}
      <RiskMatrixModal
        open={isRiskMatrixModalOpen}
        onClose={() => setIsRiskMatrixModalOpen(false)}
        onSelect={handleRiskMatrixSelect}
        selectedSeverityIndex={riskRatingSeverityIndex}
        selectedLikelihoodIndex={riskRatingLikelihoodIndex}
      />

      {/* Open Gap Confirmation Modal */}
      <ConfirmModal
        open={openGapModalOpen}
        title="Open this GAP?"
        description={
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '12px' }}>
              Are you sure you want to open this GAP?
            </div>
            <div style={{ color: '#6B7280' }}>
              Once opened, a permanent GAP ID will be assigned and the status cannot be reverted to Draft.
            </div>
          </div>
        }
        confirmText="Open GAP"
        cancelText="Cancel"
        loading={isSavingAsActive}
        onConfirm={handleConfirmOpenGap}
        onClose={() => setOpenGapModalOpen(false)}
      />

      {/* Archive Gap Confirmation Modal */}
      <ConfirmModal
        open={archiveGapModalOpen}
        title="Archive this GAP?"
        description={
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '12px' }}>
              Are you sure you want to archive this GAP?
            </div>
            <div style={{ color: '#6B7280' }}>
              The GAP Closure Date will be set to today. An archived GAP cannot be re-opened.
            </div>
          </div>
        }
        confirmText="Archive GAP"
        cancelText="Cancel"
        loading={isArchiving}
        onConfirm={handleConfirmArchive}
        onClose={() => setArchiveGapModalOpen(false)}
      />
    </PageContainer>
  );
};

export default GapItemPage;
